import { useEffect, useRef, type ChangeEvent, type ClipboardEvent } from 'react';

export const CODE_LENGTH = 6;

/**
 * The 6-digit verification code field.
 *
 * ONE input, not six boxes. The design system has exactly one input primitive
 * (Field), so six bespoke single-character boxes would be a new treatment to
 * maintain; a single input also keeps `autocomplete="one-time-code"` working
 * (splitting a code across inputs is a well-known way to break iOS/Android
 * autofill), stays trivially accessible with one real <label>, needs no
 * focus-shuffling keyboard logic, and shrinks rather than wraps down to 360px.
 * Paste of a full code therefore just works, with no handler at all.
 *
 * It only LOOKS like a code field: the border/background/focus-ring classes are
 * copied from Field so it sits in the same family, with the mono token and wide
 * tracking doing the rest.
 */
export default function CodeInput({
  value,
  onChange,
  onComplete,
  disabled,
  error,
  describedBy,
  autoFocus = true,
  refocusToken = 0,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Fired once the 6th digit lands — the form auto-submits rather than making
   *  the user reach for a button they were always going to press. */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  describedBy?: string;
  autoFocus?: boolean;
  /** Bump to pull focus back. The caller raises it after a rejected code, so the
   *  cleared field is ready to retype into without reaching for the mouse. */
  refocusToken?: number;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // onComplete lives in a ref so firing it never depends on the caller
  // memoising the handler.
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (refocusToken > 0) ref.current?.focus();
  }, [refocusToken]);

  function digitsOnly(raw: string): string {
    return raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const next = digitsOnly(e.target.value);
    onChange(next);
    if (next.length === CODE_LENGTH) completeRef.current?.(next);
  }

  // Paste needs its own handler only because a pasted "048 291" or "Code: 048291"
  // must be scrubbed before it lands; the default paste would insert it verbatim.
  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = digitsOnly(e.clipboardData.getData('text'));
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    if (pasted.length === CODE_LENGTH) completeRef.current?.(pasted);
  }

  return (
    <input
      ref={ref}
      id="code"
      name="code"
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="one-time-code"
      maxLength={CODE_LENGTH}
      value={value}
      onChange={handleChange}
      onPaste={handlePaste}
      disabled={disabled}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      placeholder="000000"
      className={
        'w-full rounded-md border bg-[var(--cad-panel-bg,#18181b)] px-3 py-3 text-center ' +
        'font-mono text-2xl tracking-[0.5em] text-[var(--cad-text,#f4f4f5)] outline-none ' +
        // The tracking adds a trailing gap after the last glyph; the indent
        // cancels it so the digits read as centred.
        'indent-[0.5em] tabular-nums disabled:opacity-60 ' +
        (error
          ? 'border-[var(--cad-danger,#f87171)]'
          : 'border-[var(--cad-panel-border,#3f3f46)] focus:border-[var(--cad-accent,#6366f1)]')
      }
    />
  );
}
